import { AccessTime, Send } from "@mui/icons-material";
import { Alert, Grid } from "@mui/material";
import moment from "moment";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Commenter } from "../../components/Articles/Commenter";
import { AuthorModal } from "../../components/Global/AuthorModal";
import { PageHero } from "../../components/Global/PageHero";
import { BackToTop } from "../../templates/BackToTop";
import { CustomButton } from "../../templates/CustomButton";
import { CustomInput, CustomTextArea } from "../../templates/CustomInput";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  increment,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

const AuthorAvatar = ({ src, name }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="w-9 h-9 rounded-full object-cover shrink-0"
        style={{ border: "2px solid #ffc000" }}
      />
    );
  }
  return (
    <svg
      viewBox="0 0 32 32"
      className="w-9 h-9 rounded-full shrink-0"
      style={{ background: "rgba(255,192,0,0.12)" }}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="16" cy="12" r="5" fill="#ffc000" opacity="0.8" />
      <path
        d="M6 26c0-5.523 4.477-10 10-10s10 4.477 10 10"
        stroke="#ffc000"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.8"
      />
    </svg>
  );
};

export const ViewArticle = () => {
  const params = useParams();

  const [article, setArticle] = useState(null);
  const [articleComments, setArticleComments] = useState([]);
  const [noOfElement, setnoOfElement] = useState(5);
  const hasMore = articleComments.length > noOfElement;
  const [errorMsg, setErrorMsg] = useState("");
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [authorModal, setAuthorModal] = useState(false);

  const unsubscribeRef = useRef(null);

  // Fetch article once
  useEffect(() => {
    const getArticle = async () => {
      try {
        const q = query(
          collection(db, "articles"),
          where("slug", "==", params.slug),
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          setArticle({
            _id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate() || new Date(),
          });
        }
      } catch (err) {
        console.error(err.message);
      }
    };
    getArticle();
  }, [params.slug]);

  // Real-time comments listener — sets up when article._id is known
  useEffect(() => {
    if (!article?._id) return;

    // clean up any previous listener
    if (unsubscribeRef.current) unsubscribeRef.current();

    const q = query(
      collection(db, "comments"),
      where("articleId", "==", article._id),
    );

    unsubscribeRef.current = onSnapshot(
      q,
      (snap) => {
        const comments = snap.docs
          .map((d) => ({
            _id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate() || new Date(),
          }))
          .sort((a, b) => b.createdAt - a.createdAt);
        setArticleComments(comments);
      },
      (err) => console.error("Comments listener error:", err.message),
    );

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [article?._id]);

  // show noOfElement + 1 so we can blur the peek comment
  const slice = articleComments.slice(0, noOfElement + (hasMore ? 1 : 0));

  const submitComment = async () => {
    if (!name) return setErrorMsg("Name cannot be empty");
    if (!comment) return setErrorMsg("Comment cannot be empty");

    setSubmitting(true);
    try {
      await addDoc(collection(db, "comments"), {
        name,
        articleId: article._id,
        comments: comment,
        createdAt: serverTimestamp(),
      });

      updateDoc(doc(db, "articles", article._id), {
        commentCount: increment(1),
      }).catch(() => {});

      setName("");
      setComment("");
      setErrorMsg("");
      toast("Comment added!", { type: "success" });
    } catch (err) {
      console.error(err.message);
      setErrorMsg("Error submitting comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fragment>
      <AuthorModal open={authorModal} onClose={() => setAuthorModal(false)} authorName={article?.author} />
      {/* <ToastContainer /> */}
      <PageHero
        eyebrow="Article"
        title={article?.title || ""}
        subtitle={article?.phrase}
      />

      <div className="px-2 py-4 md:px-6 md:py-16 lg:px-20 lg:py-20">
        <img
          src={article?.imgSrc}
          alt={article?.title}
          className="w-full h-72 md:h-[600px] object-cover"
        />

        {/* Author + date row */}
        <div className="py-4 flex items-center gap-6">
          <button
            onClick={() => setAuthorModal(true)}
            className="flex items-center gap-2 hover:opacity-70 transition-opacity"
          >
            <AuthorAvatar src={article?.authorImg} name={article?.author} />
            <p className="font-aref font-bold text-sm md:text-base text-primary">
              {article?.author}
            </p>
          </button>
          <div className="flex items-center gap-1.5 text-gray-400">
            <AccessTime fontSize="small" />
            <p className="font-abel text-sm md:text-base text-primary">
              {article?.createdAt
                ? moment(article.createdAt).format("MMM Do, YYYY")
                : ""}
            </p>
          </div>
        </div>

        <div className="py-6">
          {article?.phrase && (
            <div className="mb-10 py-6 px-2 md:py-12 md:px-20 bg-gray-100 flex items-center gap-4 md:gap-8 relative">
              <div className="border-l-8 border-custom h-full left-0 absolute" />
              <div>
                <img
                  src="https://www.pngmart.com/files/3/Quotation-PNG-Transparent-Image.png"
                  alt=""
                  className="w-12 h-8 ml-2 md:ml-0 md:w-16 md:h-12"
                />
              </div>
              <p className="font-abel italic text-sm md:text-xl">
                {article.phrase}
              </p>
            </div>
          )}
          <div className="eventBody font-abel text-lg">
            <div dangerouslySetInnerHTML={{ __html: article?.body }} />
          </div>
        </div>
      </div>

      {/* Comments section */}
      <div className="bg-gray-100 pb-12">
        <div className="px-5 md:px-4 lg:px-20 py-10 flex flex-col gap-4">
          {articleComments.length > 0 && (
            <p className="font-aref text-primary text-lg">
              {articleComments.length} Comment
              {articleComments.length !== 1 ? "s" : ""}
            </p>
          )}

          {slice.map((c, index) => {
            const isPeek = hasMore && index === noOfElement; // last item is the blurred peek
            return (
              <div key={c._id || index} className="relative">
                <Commenter
                  name={c.name}
                  email={c.email}
                  time={moment(c.createdAt).format("Do MMM YYYY, h:mma")}
                  comment={c.comments}
                />
                {isPeek && (
                  <div
                    className="absolute inset-0 rounded-lg flex flex-col items-center justify-end pb-4"
                    style={{
                      background:
                        "linear-gradient(to bottom, rgba(243,244,246,0) 0%, rgba(243,244,246,0.7) 40%, rgba(243,244,246,0.97) 100%)",
                      backdropFilter: "blur(2px)",
                    }}
                  >
                    <button
                      onClick={() => setnoOfElement((n) => n + 5)}
                      className="flex items-center gap-2 font-abel text-sm font-bold px-5 py-2 rounded-full shadow-md transition-all hover:shadow-lg"
                      style={{ background: "#ffc000", color: "#fff" }}
                    >
                      Show more comments
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Show more button when there's no peek (exact multiple) */}
          {!hasMore &&
            articleComments.length > 5 &&
            noOfElement >= articleComments.length && (
              <button
                onClick={() => setnoOfElement(5)}
                className="self-center font-abel text-sm text-gray-400 hover:text-gray-600 underline mt-2"
              >
                Show less
              </button>
            )}
        </div>

        {errorMsg && (
          <Alert severity="error" className="mx-5 md:mx-20">
            {errorMsg}
          </Alert>
        )}

        {/* Leave a comment */}
        <div className="px-5 md:px-20 py-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8" style={{ background: "#ffc00088" }} />
            <p className="font-abel uppercase tracking-[0.4em] text-xs" style={{ color: "#ffc000" }}>
              Join the conversation
            </p>
            <div className="h-px w-8" style={{ background: "#ffc00088" }} />
          </div>
          <h3 className="font-aref font-bold text-2xl mb-6" style={{ color: "#000c66" }}>
            Leave a <span style={{ color: "#ffc000" }}>Comment</span>
          </h3>

          <div className="flex flex-col gap-4" style={{ maxWidth: 560 }}>
            <CustomInput
              label="Name"
              type="text"
              className="w-full h-fit bg-white"
              onChange={(e) => { setName(e.target.value); setErrorMsg(""); }}
              value={name}
            />
            <CustomTextArea
              label="Your Comment"
              rowNo={4}
              className="w-full h-fit bg-white"
              onChange={(e) => { setComment(e.target.value); setErrorMsg(""); }}
              value={comment}
            />
            <div>
              <CustomButton
                name={submitting ? "Submitting..." : "Submit"}
                icon={<Send className="ml-2" />}
                onClick={submitComment}
                disabled={submitting}
              />
            </div>
          </div>
        </div>
      </div>

      <BackToTop />
    </Fragment>
  );
};
